/**
 * HIPAA session lifecycle helpers.
 *
 * Provides:
 *   - startIdleWarning(): the idle WARNING clock behind IdleWarningModal.
 *     It fires onWarn() so the modal can show its countdown; the modal performs
 *     the actual hard-logout at 0 (Task #79 PR-1). P110: the threshold is no
 *     longer a constant here — it is derived from the server's policy by
 *     warnMsFromPolicy(), so warning and logoff cannot disagree.
 *   - shouldRequireFullReauth() / BACKGROUND_REAUTH_THRESHOLD_MS: a pure
 *     time-since-background heuristic for the mobile re-auth policy.
 *
 * NOTE (Task #79 PR-1): the former startSessionLifecycle()/onIdleLogout export
 * was removed — it was never wired anywhere (dead code) and having it alongside
 * the warning modal implied two competing idle-logout paths. The ONE idle-logout
 * mechanism is now: IdleWarningModal (warning at idle−2 min → hard logout at
 * the server's idle limit, 28 → 30 min as of P110),
 * backed by apiClient.js's heartbeat idle check and the server-authoritative
 * idle clock. §164.312(a)(2)(iii).
 */

// P110: there is no local idle number any more. The server owns it
// (database/sessionPolicy.js → GET /api/session-policy) and the caller passes
// the warn threshold in. DEFAULT_IDLE_MS used to be a fourth copy of "15 min";
// keeping it would have been the exact drift this card removes.

/**
 * Time-since-background heuristic — caller passes it to decide whether to
 * require full re-auth (long absence) vs biometric unlock (short absence).
 *
 * Default policy: > 5 min backgrounded → full re-auth, otherwise biometric.
 * Tunable per deployment.
 */
export const BACKGROUND_REAUTH_THRESHOLD_MS = 5 * 60 * 1000;

export function shouldRequireFullReauth(backgroundedAt, threshold = BACKGROUND_REAUTH_THRESHOLD_MS) {
  if (!backgroundedAt) return false;
  return Date.now() - backgroundedAt > threshold;
}

export const SESSION_LIFECYCLE_DEFAULTS = Object.freeze({
  backgroundReauthMs: BACKGROUND_REAUTH_THRESHOLD_MS,
});

/**
 * Warn threshold derived from the SERVER's policy — the single arithmetic
 * place where "warn two minutes before logout" is expressed.
 *
 * Clamped to a positive value: if a future policy ever set idleTimeoutMs at or
 * below warnBeforeMs, a non-positive timeout would fire setTimeout immediately
 * and pin the dialog open forever. Half the window is the degenerate-case
 * answer — still a warning, still before the logoff.
 *
 * @param {{idleTimeoutMs:number, warnBeforeMs:number}} policy
 * @returns {number} ms of inactivity after which the warning should appear
 */
export function warnMsFromPolicy(policy) {
  const idle = Number(policy?.idleTimeoutMs);
  const lead = Number(policy?.warnBeforeMs);
  if (!Number.isFinite(idle) || idle <= 0) return 0;
  if (!Number.isFinite(lead) || lead <= 0) return idle;
  return idle > lead ? idle - lead : Math.floor(idle / 2);
}

// ── Idle Warning ──────────────────────────────────────────────────────────────

// P110: no module-level default. startIdleWarning() now REQUIRES warnMs,
// computed by warnMsFromPolicy() from the server's answer (28 min against the
// 30-min timeout). A default here would silently paper over a failed policy
// fetch with a stale 14, which is how the client and server drifted before.

// Superset of common "am I still here?" gestures. scroll and mouseup cover the
// most common ones without duplicating the pointer-down events already tracked
// by the heartbeat layer.
export const WARN_EVENTS = ['mouseup', 'keydown', 'scroll', 'touchstart'];

/**
 * Track user idle time and fire a warning callback before the hard-logout
 * threshold. Client-side UX warning only — the actual logout is performed by
 * IdleWarningModal (at countdown 0) and enforced server-side.
 *
 * @param {object}      opts
 * @param {number}      opts.warnMs    idle threshold, from warnMsFromPolicy()
 * @param {Function}    opts.onWarn    — called when idle threshold reached
 * @param {Function}    [opts.onReset] — called when activity resets an active warning
 * @param {EventTarget} [opts._target] — override event target (inject for testing)
 *
 * @returns {{ reset: Function, stop: Function }}
 *   reset — call to dismiss the warning and restart the idle clock (e.g. keepalive)
 *   stop  — call to tear down listeners and cancel pending timer (cleanup)
 */
export function startIdleWarning({ warnMs, onWarn, onReset, _target } = {}) {
  if (typeof onWarn !== 'function') {
    throw new TypeError('startIdleWarning: onWarn callback is required');
  }
  if (!Number.isFinite(warnMs) || warnMs <= 0) {
    throw new TypeError('startIdleWarning: warnMs must be a positive number (from warnMsFromPolicy)');
  }

  const target = _target || window;
  let timer = null;
  let warned = false;

  function reset() {
    if (timer) clearTimeout(timer);
    if (warned) {
      warned = false;
      try { if (typeof onReset === 'function') onReset(); } catch { /* swallow */ }
    }
    timer = setTimeout(() => {
      timer = null;
      warned = true;
      try { onWarn(); } catch { /* swallow */ }
    }, warnMs);
  }

  WARN_EVENTS.forEach((e) => target.addEventListener(e, reset, { passive: true }));
  reset(); // start the clock immediately

  return {
    reset,
    stop() {
      if (timer) clearTimeout(timer);
      timer = null;
      WARN_EVENTS.forEach((e) => target.removeEventListener(e, reset));
    },
  };
}
